import React, { useCallback, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { useHistory } from 'react-router-dom';

import CustomLoading from '../../../util_components/custom_loading';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import SummaryCards from '../shared/summary_cards';
import DataTable from '../shared/data_table';
import TeacherSlugLine from '../shared/teacher_slug_line';
import { toggle_sort } from '../shared/use_sortable';
import ShadowBanner from './shadow_banner';
import { fetch_csv, fetch_l1 } from '../api';
import {
  csv_filename,
  extract_csv_payload,
  fmt,
  fmt_signed,
  needs_review,
  needs_review_reason
} from '../format';
import { NEEDS_REVIEW_TEXT, l1_view } from './adapters';
import { shows_versions_link, versions_link } from '../close_versions/params';
import {
  TEACHER_STATUS_OPTIONS,
  apply_l1_drafts,
  apply_l1_sort,
  csv_params,
  l1_params,
  l1_state_for_month
} from './params';

/*
 * PROD-7828 R5 — Overview Level 1: one row per teacher for the selected pay month.
 * Columns follow the R5 brief: component totals + Waivers + rounded net payable (D-3).
 * Email status is intentionally absent until R11 (D-4). Rows whose month failed to compute
 * or is stale show "needs review" instead of a number.
 */
const PAGE_SIZE = 100;

const REVIEW_OPTIONS = [{ value: 'NEEDS_REVIEW', label: 'Needs review only' }];

const OverviewLevel1 = ({
  pay_month,
  month_label,
  view_state,
  on_view_state,
  on_error,
  on_open_teacher
}) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stat_cards, setStatCards] = useState([]);
  const [total_count, setTotalCount] = useState(0);
  const [shadow_banner, setShadowBanner] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // D3: drafts (applied only on Apply, house pattern), applied filters, sort and page owned by OverviewTab.
  const { applied, offset, drafts, sort } = view_state;
  const set_draft = (key, value) =>
    on_view_state((s) => ({ ...s, drafts: { ...s.drafts, [key]: value } }));

  // F4: only the latest request may render — a slow month-A response must never land under month B.
  const request_seq = useRef(0);

  const load = useCallback(
    (next_offset, filters, next_sort) => {
      request_seq.current += 1;
      const seq = request_seq.current;
      if (!pay_month) {
        // D1: never leave a previous month's rows, cards or banner on screen without a month.
        setRows([]);
        setStatCards([]);
        setTotalCount(0);
        setShadowBanner(false);
        setLoading(false);
        return;
      }
      setLoading(true);
      setStatCards([]);
      setShadowBanner(false);
      fetch_l1(l1_params({ pay_month, ...filters, sort: next_sort }, PAGE_SIZE, next_offset))
        .then((data) => {
          if (seq !== request_seq.current) return;
          const view = l1_view(data);
          setRows(view.rows);
          setStatCards(view.stat_cards);
          setTotalCount(view.total_count);
          setShadowBanner(view.shadow_banner);
          setLoading(false);
        })
        .catch((e) => {
          if (seq !== request_seq.current) return;
          on_error(e, 'Failed to load the teacher payment overview.');
          setRows([]);
          setStatCards([]);
          setTotalCount(0);
          setLoading(false);
        });
    },
    [pay_month, on_error]
  );

  // (Re)mount after Back keeps the page and sort; a month change goes to page 1. Filters need an explicit Apply.
  useEffect(() => {
    const next = l1_state_for_month(view_state, pay_month);
    if (next !== view_state) on_view_state(next);
    load(next.offset, next.applied, next.sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pay_month]);

  const apply_filters = () => {
    const next = apply_l1_drafts(view_state);
    on_view_state(next);
    load(0, next.applied, next.sort);
  };

  const go_page = (next_offset) => {
    on_view_state((s) => ({ ...s, offset: next_offset }));
    load(next_offset, applied, sort);
  };

  const on_sort = (key) => {
    const next = apply_l1_sort(view_state, toggle_sort(sort, key));
    on_view_state(next);
    load(0, next.applied, next.sort);
  };

  // D-12: the tie-out CSV is a first-class R5 button — it is the input the parity harness
  // compares against the manual sheet. Server-generated so the export never diverges from
  // the stored numbers (and never re-derives anything client-side).
  const download_csv = () => {
    if (downloading || !pay_month) return;
    setDownloading(true);
    fetch_csv(csv_params({ pay_month, ...applied, sort }))
      .then((body) => {
        const csv = extract_csv_payload(body);
        if (!csv) {
          on_error(new Error('empty csv'), 'The CSV export came back empty.');
          setDownloading(false);
          return;
        }
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), csv_filename(pay_month));
        setDownloading(false);
      })
      .catch((e) => {
        on_error(e, 'Failed to download the tie-out CSV.');
        setDownloading(false);
      });
  };

  const amount_cell = (row, value, tone) => {
    if (needs_review(row)) {
      return (
        <span className="tp-neg" title={needs_review_reason(row)}>
          {NEEDS_REVIEW_TEXT}
        </span>
      );
    }
    return <span className={tone}>{value}</span>;
  };

  // D111 C10: closed now or closed before (reopen_count); a never-closed month has no versions.
  const versions_button = (t) =>
    shows_versions_link(t) ? (
      <button
        type="button"
        className="tp-link"
        aria-label={`Versions of ${t.teacher_name}'s month`}
        onClick={() => history.push(versions_link(pay_month, t.teacher_uuid))}
      >
        Versions
      </button>
    ) : null;

  const columns = [
    {
      key: 'teacher',
      label: 'Teacher',
      hideInCard: true,
      sort_key: 'teacher_name',
      render: (t) => (
        <button type="button" className="tp-link" onClick={() => on_open_teacher(t)}>
          <div>{t.teacher_name || '--'}</div>
          <TeacherSlugLine slug={t.teacher_slug} />
        </button>
      )
    },
    {
      key: 'one_on_one_amount',
      label: '1-on-1',
      align: 'right',
      sort_key: 'one_on_one',
      render: (t) => amount_cell(t, fmt(t.one_on_one_amount), 'tp-pos')
    },
    {
      key: 'group_class_amount',
      label: 'GC',
      align: 'right',
      sort_key: 'group_class',
      render: (t) => amount_cell(t, fmt(t.group_class_amount), 'tp-pos')
    },
    {
      key: 'trial_amount',
      label: 'Trial',
      align: 'right',
      sort_key: 'trial',
      render: (t) => amount_cell(t, fmt(t.trial_amount), 'tp-pos')
    },
    {
      key: 'incentive_amount',
      label: 'Incentives',
      align: 'right',
      sort_key: 'incentives',
      render: (t) => amount_cell(t, fmt_signed(t.incentive_amount), 'tp-pos')
    },
    {
      key: 'additional_amount',
      label: 'Add. payments',
      align: 'right',
      sort_key: 'additional',
      render: (t) =>
        amount_cell(
          t,
          fmt_signed(t.additional_amount),
          Number(t.additional_amount) < 0 ? 'tp-neg' : 'tp-pos'
        )
    },
    {
      key: 'penalty_amount',
      label: 'Penalties',
      align: 'right',
      sort_key: 'penalties',
      render: (t) => amount_cell(t, fmt(t.penalty_amount), 'tp-neg')
    },
    {
      key: 'waived_penalty_amount',
      label: 'Waivers',
      align: 'right',
      sort_key: 'waivers',
      render: (t) => amount_cell(t, fmt(t.waived_penalty_amount), 'tp-pos')
    },
    {
      key: 'rounded_net_payable',
      label: 'Net payable',
      align: 'right',
      sort_key: 'net',
      render: (t) => amount_cell(t, fmt(t.rounded_net_payable), 'tp-strong')
    },
    {
      key: 'actions',
      label: 'Actions',
      hideInCard: true,
      render: (t) => (
        <span className="tp-actions">
          <button type="button" className="tp-link" onClick={() => on_open_teacher(t)}>
            View
          </button>
          {versions_button(t)}
        </span>
      )
    }
  ];

  const page_start = total_count === 0 ? 0 : offset + 1;
  const page_end = Math.min(offset + rows.length, total_count);
  const prev_off = offset <= 0;
  const next_off = offset + PAGE_SIZE >= total_count;

  return (
    <div>
      <ShadowBanner show={shadow_banner} />

      <SummaryCards cards={stat_cards} />

      <div className="tp-frow">
        <input
          className="tp-input"
          placeholder="Teacher name or email"
          value={drafts.search}
          onChange={(e) => set_draft('search', e.target.value)}
          aria-label="Teacher search"
        />
        <div style={{ width: '200px' }}>
          <CustomSingleSelect
            label="Rows"
            noLabel
            options={REVIEW_OPTIONS}
            value={drafts.review}
            defaultOption="All teachers"
            onChange={(e) => set_draft('review', e.target.value)}
          />
        </div>
        <div style={{ width: '220px' }}>
          <CustomSingleSelect
            label="Teacher status"
            noLabel
            options={TEACHER_STATUS_OPTIONS}
            value={drafts.status}
            defaultOption="All teacher statuses"
            onChange={(e) => set_draft('status', e.target.value)}
          />
        </div>
        <button type="button" className="tp-btn-out" onClick={apply_filters}>
          Apply
        </button>
        <div className="tp-frow-right">
          {/* aria-disabled, not disabled: the pressed button keeps focus while it prepares (CD-114). */}
          <button
            type="button"
            className="tp-btn-out"
            onClick={download_csv}
            aria-disabled={downloading || !pay_month ? 'true' : undefined}
          >
            {downloading ? 'Preparing…' : '↓ Tie-out CSV'}
          </button>
        </div>
      </div>

      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-title">
            All teachers · {month_label}{' '}
            <span className="tp-card-hd-sub">
              {total_count === 0
                ? 'No teachers'
                : `Showing ${page_start}–${page_end} of ${total_count}`}
            </span>
          </span>
          <span className="tp-card-hd-sub">Click a teacher to open the breakdown</span>
        </div>
        {loading ? (
          <CustomLoading />
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(t) => t.teacher_uuid}
            sort={sort}
            on_sort={on_sort}
            emptyText="No teacher payment rows for this month and filter."
            cardTitle={(t) => (
              <>
                <button
                  type="button"
                  className="tp-link"
                  style={{ fontSize: 14 }}
                  onClick={() => on_open_teacher(t)}
                >
                  {t.teacher_name || '--'}
                </button>
                {versions_button(t)}
              </>
            )}
          />
        )}
      </div>

      {total_count > PAGE_SIZE ? (
        <div className="flex items-center gap-3 justify-center mt-3">
          {/* aria-disabled, not disabled: Next on the last page keeps focus (CD-114). */}
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={prev_off ? undefined : () => go_page(Math.max(0, offset - PAGE_SIZE))}
            aria-disabled={prev_off ? 'true' : undefined}
          >
            Prev
          </button>
          <span className="tp-card-hd-sub">
            {page_start}–{page_end} / {total_count}
          </span>
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={next_off ? undefined : () => go_page(offset + PAGE_SIZE)}
            aria-disabled={next_off ? 'true' : undefined}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default OverviewLevel1;
